import React, { useEffect, useState } from "react";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";

/**
 * Klubin omat säännöt ja ohjeet (docs/ideat.md H2, kerros 2).
 *
 * Teksti asuu klubidokumentin `clubRules`-kentässä eikä `private/`-
 * alikokoelmassa: se ei ole salaisuus. Klubidokumentin säännöt kattavat
 * jo tarvittavan — klubilainen lukee, ylläpitäjä kirjoittaa — joten
 * uutta Firestore-sääntöä ei tarvita.
 *
 * Tämä on se tieto, jonka varassa tuomari vastaa klubia koskeviin
 * kysymyksiin. Mitä tähän ei ole kirjattu, sitä tuomari ei keksi.
 */
export default function ClubRulesAdmin({ clubId, lang }: {
  clubId: string;
  lang: "fi" | "en";
}) {
  const [text, setText]   = useState("");
  const [draft, setDraft] = useState("");
  const [editing, setEditing] = useState(false);
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState("");
  const fi = lang === "fi";

  useEffect(() => {
    let alive = true;
    getDoc(doc(db, "clubs", clubId)).then(snap => {
      if (!alive) return;
      const v = snap.data()?.clubRules;
      setText(typeof v === "string" ? v : "");
    }).catch(() => { /* lukuvirhe ei saa kaataa asetuksia */ });
    return () => { alive = false; };
  }, [clubId]);

  async function save() {
    setBusy(true); setError("");
    try {
      await updateDoc(doc(db, "clubs", clubId), { clubRules: draft.trim() });
      setText(draft.trim());
      setEditing(false);
    } catch (e) {
      const msg = (e as { message?: string })?.message;
      setError(msg || (fi ? "Tallennus epäonnistui." : "Save failed."));
    }
    setBusy(false);
  }

  const C = {
    card: "var(--sl-card)", text: "var(--sl-text)", muted: "var(--sl-muted)",
    border: "var(--sl-border)", primary: "var(--sl-primary)",
    soft: "var(--sl-soft)", error: "var(--sl-error)",
  };
  const card: React.CSSProperties = {
    background: C.card, border: `1px solid ${C.border}`,
    borderRadius: 16, padding: 18, marginBottom: 12,
  };
  const btn: React.CSSProperties = {
    padding: "10px 16px", borderRadius: 10, border: "none",
    cursor: "pointer", fontWeight: 700, fontSize: 13,
  };

  return (
    <>
      <div style={{
        fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em",
        textTransform: "uppercase", marginBottom: 16,
      }}>
        📋 {fi ? "Klubin säännöt ja ohjeet" : "Club rules and info"}
      </div>

      <div style={card}>
        <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.55, marginBottom: 12 }}>
          {fi
            ? "Omat käytännöt, aukioloajat, vuorojen jako, kenkäsäännöt, mitä tuoda mukaan. Pelaajat näkevät tämän, ja tuomari vastaa klubia koskeviin kysymyksiin tämän perusteella — mitä tähän ei ole kirjattu, sitä se ei keksi."
            : "Your own practices, opening hours, court rotation, shoe rules, what to bring. Players can see this, and the referee answers club questions from it — what is not written here, it will not invent."}
        </div>

        {editing ? (
          <div>
            <textarea
              value={draft}
              onChange={e => setDraft(e.target.value)}
              rows={10}
              maxLength={4000}
              autoFocus
              placeholder={fi
                ? "Esim.\nSali on auki ma–to 17–22.\nVuorot jaetaan ilmoittautumisjärjestyksessä.\nSisäkengät pakolliset.\nPallot löytyvät kaapista."
                : "E.g.\nOpen Mon–Thu 17–22.\nCourts are allocated in sign-up order.\nIndoor shoes required."}
              style={{
                width: "100%", boxSizing: "border-box", padding: "11px 13px",
                background: "var(--sl-bg)", border: `1.5px solid ${C.border}`,
                borderRadius: 10, fontSize: 14, color: C.text,
                fontFamily: "'Inter', sans-serif", outline: "none",
                resize: "vertical", marginBottom: 6,
              }}
            />
            <div style={{ fontSize: 11, color: C.muted, marginBottom: 10 }}>
              {draft.length} / 4000
            </div>
            {error && (
              <div style={{ fontSize: 12, color: C.error, marginBottom: 10, lineHeight: 1.45 }}>
                {error}
              </div>
            )}
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={save} disabled={busy} style={{
                ...btn, flex: 1, background: C.primary, color: "#fff",
                opacity: busy ? 0.6 : 1,
              }}>
                {busy ? (fi ? "Tallennetaan…" : "Saving…") : (fi ? "Tallenna" : "Save")}
              </button>
              <button onClick={() => { setEditing(false); setError(""); }} style={{
                ...btn, flex: 1, background: C.soft, color: C.text,
              }}>
                {fi ? "Peruuta" : "Cancel"}
              </button>
            </div>
          </div>
        ) : (
          <div>
            {text ? (
              <div style={{
                fontSize: 13, lineHeight: 1.6, color: C.text,
                whiteSpace: "pre-wrap", marginBottom: 12,
              }}>
                {text}
              </div>
            ) : (
              <div style={{ fontSize: 13, color: C.muted, marginBottom: 12 }}>
                {fi ? "Ei vielä kirjattu." : "Nothing written yet."}
              </div>
            )}
            <button onClick={() => { setDraft(text); setEditing(true); }} style={{
              ...btn, background: C.soft, color: C.text,
            }}>
              {text
                ? (fi ? "✏️ Muokkaa" : "✏️ Edit")
                : (fi ? "+ Lisää" : "+ Add")}
            </button>
          </div>
        )}
      </div>
    </>
  );
}
